import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  ContactListItem,
  LigneVente,
  LigneVenteInput,
  ModePaiement,
  StatutPaiement,
  TypeClient,
  Vente,
} from '@cockpit/shared';
import { ModesPaiement, StatutsPaiement, TypesClient } from '@cockpit/shared';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { ContactAutocomplete } from './ContactAutocomplete';
import { ContactLegerModal } from './ContactLegerModal';
import { LignesVenteEditor } from './LignesVenteEditor';
import {
  LABEL_MODE_PAIEMENT,
  LABEL_STATUT_PAIEMENT,
  LABEL_TYPE_CLIENT,
  formatEuro,
  isoToDateInput,
  parseDateToIso,
  parsePctToCentiemes,
} from '../../lib/format';

interface InitialData {
  vente: Vente;
  lignes: LigneVente[];
  contact: ContactListItem | null;
}

interface Props {
  initial?: InitialData;
  submitting: boolean;
  onSubmit: (payload: {
    vente: {
      numero_facture_henrri: string | null;
      contact_id: number;
      date_vente: string;
      date_emission: string;
      date_echeance: string | null;
      date_encaissement: string | null;
      type_client_override: TypeClient | null;
      mode_paiement: ModePaiement | null;
      statut_paiement: StatutPaiement;
      adresse_livraison: string | null;
      code_postal_livraison: string | null;
      ville_livraison: string | null;
      remise_globale_pct: number;
      notes: string | null;
    };
    lignes: LigneVenteInput[];
  }) => void;
  onCancel?: () => void;
  submitLabel?: string;
}

function ligneVenteToInput(l: LigneVente): LigneVenteInput {
  return {
    id: l.id,
    reference_id: l.reference_id,
    reference_libre: l.reference_libre,
    largeur_cm: l.largeur_cm,
    longueur_cm: l.longueur_cm,
    m2_centi: l.m2_centi,
    nb_rouleaux_entames: l.nb_rouleaux_entames,
    prix_unitaire_centimes_par_m2: l.prix_unitaire_centimes_par_m2,
    remise_ligne_pct: l.remise_ligne_pct,
    taux_tva: l.taux_tva,
    cout_unitaire_centimes_par_m2: l.cout_unitaire_centimes_par_m2,
    cout_unitaire_estime_centimes_par_m2: l.cout_unitaire_estime_centimes_par_m2,
    ordre: l.ordre,
  };
}

export function VenteForm({
  initial,
  submitting,
  onSubmit,
  onCancel,
  submitLabel = 'Créer la vente',
}: Props): JSX.Element {
  const [contact, setContact] = useState<ContactListItem | null>(initial?.contact ?? null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalInitial, setContactModalInitial] = useState('');

  const [numFacture, setNumFacture] = useState(initial?.vente.numero_facture_henrri ?? '');
  const [dateVente, setDateVente] = useState(
    isoToDateInput(initial?.vente.date_vente) || todayIso(),
  );
  const [dateEmission, setDateEmission] = useState(
    isoToDateInput(initial?.vente.date_emission) || todayIso(),
  );
  const [dateEcheance, setDateEcheance] = useState(isoToDateInput(initial?.vente.date_echeance));
  const [dateEncaissement, setDateEncaissement] = useState(
    isoToDateInput(initial?.vente.date_encaissement),
  );
  const [typeClientOverride, setTypeClientOverride] = useState<TypeClient | ''>(
    initial?.vente.type_client_override ?? '',
  );
  const [modePaiement, setModePaiement] = useState<ModePaiement | ''>(
    initial?.vente.mode_paiement ?? '',
  );
  const [statutPaiement, setStatutPaiement] = useState<StatutPaiement>(
    initial?.vente.statut_paiement ?? 'En_attente',
  );
  const [adresseLivraison, setAdresseLivraison] = useState(initial?.vente.adresse_livraison ?? '');
  const [cpLivraison, setCpLivraison] = useState(initial?.vente.code_postal_livraison ?? '');
  const [villeLivraison, setVilleLivraison] = useState(initial?.vente.ville_livraison ?? '');
  const [remiseGlobale, setRemiseGlobale] = useState(
    String((initial?.vente.remise_globale_pct ?? 0) / 100),
  );
  const [notes, setNotes] = useState(initial?.vente.notes ?? '');
  const [lignes, setLignes] = useState<LigneVenteInput[]>(
    initial?.lignes.map(ligneVenteToInput) ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const totauxLocaux = useMemo(() => {
    const remiseGlobalePct = parsePctToCentiemes(remiseGlobale) ?? 0;
    let sommeHt = 0;
    let sommeTva = 0;
    let margesValides = lignes.length > 0;
    let sommeMarges = 0;
    for (const l of lignes) {
      const brut = (l.m2_centi * l.prix_unitaire_centimes_par_m2) / 100;
      const ht = Math.round((brut * (10000 - l.remise_ligne_pct)) / 10000);
      const tva = Math.round((ht * l.taux_tva) / 10000);
      sommeHt += ht;
      sommeTva += tva;
      const cout = l.cout_unitaire_centimes_par_m2 ?? l.cout_unitaire_estime_centimes_par_m2;
      if (cout === null) margesValides = false;
      else sommeMarges += ht - Math.round((l.m2_centi * cout) / 100);
    }
    const f = 10000 - remiseGlobalePct;
    const totalHt = Math.round((sommeHt * f) / 10000);
    const totalTva = Math.round((sommeTva * f) / 10000);
    return {
      remiseGlobalePct,
      totalHt,
      totalTva,
      totalTtc: totalHt + totalTva,
      marge: margesValides ? Math.round((sommeMarges * f) / 10000) : null,
    };
  }, [lignes, remiseGlobale]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setError(null);
    if (contact === null) {
      setError('Sélectionner un client.');
      return;
    }
    if (lignes.length === 0) {
      setError('Au moins une ligne produit est requise.');
      return;
    }
    const dvIso = parseDateToIso(dateVente);
    const deIso = parseDateToIso(dateEmission);
    if (dvIso === null || deIso === null) {
      setError('Dates invalides.');
      return;
    }
    onSubmit({
      vente: {
        numero_facture_henrri: numFacture.trim() === '' ? null : numFacture.trim(),
        contact_id: contact.id,
        date_vente: dvIso,
        date_emission: deIso,
        date_echeance: dateEcheance === '' ? null : parseDateToIso(dateEcheance),
        date_encaissement: dateEncaissement === '' ? null : parseDateToIso(dateEncaissement),
        type_client_override: typeClientOverride === '' ? null : typeClientOverride,
        mode_paiement: modePaiement === '' ? null : modePaiement,
        statut_paiement: statutPaiement,
        adresse_livraison: adresseLivraison.trim() === '' ? null : adresseLivraison.trim(),
        code_postal_livraison: cpLivraison.trim() === '' ? null : cpLivraison.trim(),
        ville_livraison: villeLivraison.trim() === '' ? null : villeLivraison.trim(),
        remise_globale_pct: totauxLocaux.remiseGlobalePct,
        notes: notes.trim() === '' ? null : notes.trim(),
      },
      lignes,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-slate-200">Client</h2>
          <ContactAutocomplete
            value={contact}
            onChange={setContact}
            onCreateNew={(text): void => {
              setContactModalInitial(text);
              setContactModalOpen(true);
            }}
            required
          />
          {contact !== null && (
            <Select
              label="Type de client pour cette vente (override, facultatif)"
              value={typeClientOverride}
              onChange={(e): void => setTypeClientOverride(e.target.value as TypeClient | '')}
              options={TypesClient.map((t) => ({ value: t, label: LABEL_TYPE_CLIENT[t] ?? t }))}
              placeholder="— Type du contact par défaut —"
            />
          )}
        </section>

        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-slate-200">Facture</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="N° facture Henrri (optionnel)"
              value={numFacture}
              onChange={(e): void => setNumFacture(e.target.value)}
            />
            <Input
              label="Date de la vente"
              type="date"
              value={dateVente}
              onChange={(e): void => setDateVente(e.target.value)}
              required
            />
            <Input
              label="Date d'émission"
              type="date"
              value={dateEmission}
              onChange={(e): void => setDateEmission(e.target.value)}
              required
            />
            <Input
              label="Date d'échéance"
              type="date"
              value={dateEcheance}
              onChange={(e): void => setDateEcheance(e.target.value)}
            />
            <Input
              label="Date d'encaissement"
              type="date"
              value={dateEncaissement}
              onChange={(e): void => setDateEncaissement(e.target.value)}
            />
            <Select
              label="Statut paiement"
              value={statutPaiement}
              onChange={(e): void => setStatutPaiement(e.target.value as StatutPaiement)}
              options={StatutsPaiement.map((s) => ({
                value: s,
                label: LABEL_STATUT_PAIEMENT[s] ?? s,
              }))}
            />
            <Select
              label="Mode de paiement"
              value={modePaiement}
              onChange={(e): void => setModePaiement(e.target.value as ModePaiement | '')}
              options={ModesPaiement.map((m) => ({ value: m, label: LABEL_MODE_PAIEMENT[m] ?? m }))}
              placeholder="—"
            />
            <Input
              label="Remise globale (%)"
              type="text"
              value={remiseGlobale}
              onChange={(e): void => setRemiseGlobale(e.target.value)}
            />
          </div>
        </section>

        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-slate-200">
            Livraison (si différente de l'adresse client)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Adresse"
              value={adresseLivraison}
              onChange={(e): void => setAdresseLivraison(e.target.value)}
            />
            <Input
              label="Code postal"
              value={cpLivraison}
              onChange={(e): void => setCpLivraison(e.target.value)}
            />
            <Input
              label="Ville"
              value={villeLivraison}
              onChange={(e): void => setVilleLivraison(e.target.value)}
            />
          </div>
        </section>

        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
          <LignesVenteEditor lignes={lignes} onChange={setLignes} />
        </section>

        <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-slate-200">Notes</h2>
          <textarea
            value={notes}
            onChange={(e): void => setNotes(e.target.value)}
            rows={3}
            className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-floryon-green focus:outline-none focus:ring-1 focus:ring-floryon-green/50"
          />
        </section>

        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5 flex items-center justify-between">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <div>
              <span className="text-slate-500">Total HT : </span>
              <span className="text-slate-100 font-medium">{formatEuro(totauxLocaux.totalHt)}</span>
            </div>
            <div>
              <span className="text-slate-500">TVA : </span>
              <span className="text-slate-100">{formatEuro(totauxLocaux.totalTva)}</span>
            </div>
            <div>
              <span className="text-slate-500">Total TTC : </span>
              <span className="text-floryon-green font-semibold">
                {formatEuro(totauxLocaux.totalTtc)}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Marge : </span>
              <span className="text-slate-100">{formatEuro(totauxLocaux.marge)}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {onCancel !== undefined && (
              <Button type="button" variant="secondary" onClick={onCancel}>
                Annuler
              </Button>
            )}
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Envoi…' : submitLabel}
            </Button>
          </div>
        </div>

        {error !== null && <div className="text-xs text-red-400 px-2">{error}</div>}

        <p className="text-[10px] text-slate-600 px-2">
          Calculs affichés à titre indicatif. Les totaux validés sont recalculés par le serveur.
        </p>
      </form>

      <ContactLegerModal
        isOpen={contactModalOpen}
        onClose={(): void => setContactModalOpen(false)}
        initialName={contactModalInitial}
        onCreated={(c): void => setContact(c)}
      />
    </>
  );
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
